import {
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Code,
  Container,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconAlertCircle, IconLock, IconRefresh } from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import { Option } from "functype"
import { MatchOption } from "functype-react"
import { useState } from "react"

import { Navbar } from "@/components/Navbar"
import { getServiceUrl, MONITORED_SERVICES, type ServiceId } from "@/config/services"
import { supabase } from "@/lib/supabase"

type EnvEntry =
  | { key: string; kind: "secret"; present: true; length: number; fingerprint: string }
  | { key: string; kind: "secret"; present: false }
  | { key: string; kind: "var"; present: true; value: string }
  | { key: string; kind: "var"; present: false }
  | { key: string; kind: "binding"; present: true; type: "queue" | "d1" | "rate-limit" | "unknown" }
  | { key: string; kind: "binding"; present: false }

type EnvReport = {
  environment: string
  commit: string
  timestamp: string
  groups: Record<string, EnvEntry[]>
  unknownKeys: string[]
}

type EnvironmentTarget = "development" | "production"

const SERVICE_ID: ServiceId = "agent-gate"

const fetchEnvReport = async (target: EnvironmentTarget): Promise<EnvReport> => {
  const { data, error } = await supabase.auth.getSession()
  if (error || !data.session) {
    throw new Error("Not signed in. Sign in with an admin account first.")
  }
  const token = data.session.access_token
  const url = getServiceUrl(SERVICE_ID, target === "production")
  const response = await fetch(`${url}/admin/env`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Env fetch failed: ${response.status} ${response.statusText} ${text}`)
  }
  return response.json() as Promise<EnvReport>
}

const renderStatusCell = (entry: EnvEntry) => {
  if (!entry.present) {
    return (
      <Badge color="red" variant="light">
        MISSING
      </Badge>
    )
  }
  if (entry.kind === "binding") {
    return (
      <Badge color="grape" variant="light">
        binding · {entry.type}
      </Badge>
    )
  }
  return (
    <Badge color="green" variant="light">
      present
    </Badge>
  )
}

const renderValueCell = (entry: EnvEntry) => {
  if (!entry.present) return <Text c="dimmed">—</Text>
  if (entry.kind === "secret") {
    return <Code>{entry.fingerprint}</Code>
  }
  if (entry.kind === "var") {
    return <Code style={{ wordBreak: "break-all" }}>{entry.value}</Code>
  }
  return <Text c="dimmed">—</Text>
}

const renderLengthCell = (entry: EnvEntry) => {
  if (entry.present && entry.kind === "secret") return <Text size="sm">{entry.length}</Text>
  if (entry.present && entry.kind === "var") return <Text size="sm">{entry.value.length}</Text>
  return <Text c="dimmed">—</Text>
}

const GroupCard = ({ group, entries }: { group: string; entries: EnvEntry[] }) => (
  <Card withBorder shadow="sm" padding="md" radius="md">
    <Title order={4} tt="capitalize" mb="sm">
      {group}
    </Title>
    <Table striped highlightOnHover withRowBorders={false}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Key</Table.Th>
          <Table.Th>Kind</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Length</Table.Th>
          <Table.Th>Value / Fingerprint</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map((entry) => (
          <Table.Tr key={entry.key}>
            <Table.Td>
              <Code>{entry.key}</Code>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">
                {entry.kind}
              </Text>
            </Table.Td>
            <Table.Td>{renderStatusCell(entry)}</Table.Td>
            <Table.Td>{renderLengthCell(entry)}</Table.Td>
            <Table.Td>{renderValueCell(entry)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Card>
)

export function EnvInspectorPage() {
  const [target, setTarget] = useState<EnvironmentTarget>("development")

  const query = useQuery({
    queryKey: ["admin-env", target],
    queryFn: () => fetchEnvReport(target),
    staleTime: 60_000,
    retry: 1,
  })

  const handleRefresh = () => {
    void query.refetch()
    notifications.show({ title: "Refreshing…", message: `Re-fetching ${target} env`, color: "blue" })
  }

  const targetUrl = MONITORED_SERVICES[SERVICE_ID][target === "production" ? "productionUrl" : "developmentUrl"]
  const groupOrder = ["core", "oauth", "stripe", "monitoring", "tester", "email", "features", "bindings"]

  return (
    <AppShell padding="md">
      <Navbar onRefresh={handleRefresh} isRefreshing={query.isFetching} />

      <AppShell.Main>
        <Container size="xl" py="md">
          <Group justify="space-between" mb="lg">
            <Box>
              <Title order={2}>Env Inspector</Title>
              <Text c="dimmed" size="sm">
                Read-only view of secrets and variables loaded on the running Worker. Secrets show length + last-4
                fingerprint only.
              </Text>
            </Box>
            <Group>
              <SegmentedControl
                value={target}
                onChange={(v) => setTarget(v as EnvironmentTarget)}
                data={[
                  { label: "dev", value: "development" },
                  { label: "prod", value: "production" },
                ]}
              />
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={query.isFetching}
                variant="default"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          <Alert color="yellow" icon={<IconLock size={16} />} mb="md">
            Auth: your Supabase admin session token is sent on each request. Backend re-verifies the JWT and re-checks{" "}
            <Code>users.role</Code> server-side. Secrets are masked to last-4 chars. Treat what you see as sensitive —
            closing the tab does not clear browser cache.
          </Alert>

          {query.isLoading && (
            <Center py="xl">
              <Box ta="center">
                <Loader size="lg" mb="md" />
                <Text>Loading env report from {targetUrl}…</Text>
              </Box>
            </Center>
          )}

          {query.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Failed to load env report">
              {query.error.message}
            </Alert>
          )}

          <MatchOption
            value={Option(query.data)}
            None={() => null}
            Some={(report) => (
              <Stack gap="md">
                <Card withBorder padding="md" radius="md">
                  <Group gap="xl">
                    <Box>
                      <Text size="xs" c="dimmed">
                        Environment
                      </Text>
                      <Text fw={600}>{report.environment}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">
                        Commit
                      </Text>
                      <Code>{report.commit}</Code>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">
                        Backend URL
                      </Text>
                      <Code>{targetUrl}</Code>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">
                        Generated
                      </Text>
                      <Text size="sm">{new Date(report.timestamp).toLocaleString()}</Text>
                    </Box>
                  </Group>
                </Card>

                {groupOrder
                  .filter((g) => report.groups[g] && report.groups[g]!.length > 0)
                  .map((group) => (
                    <GroupCard key={group} group={group} entries={report.groups[group]!} />
                  ))}

                {report.unknownKeys.length > 0 && (
                  <Card withBorder padding="md" radius="md">
                    <Title order={4} mb="sm">
                      Unknown keys at runtime
                    </Title>
                    <Text size="sm" c="dimmed" mb="sm">
                      Keys present on the Worker that are not declared in <Code>EXPECTED_KEYS</Code>. Update the backend
                      list when adding a new secret.
                    </Text>
                    <Group gap="xs">
                      {report.unknownKeys.map((k) => (
                        <Code key={k}>{k}</Code>
                      ))}
                    </Group>
                  </Card>
                )}
              </Stack>
            )}
          />
        </Container>
      </AppShell.Main>
    </AppShell>
  )
}
